import React, { FC, PropsWithChildren, useState, useEffect, useRef } from 'react';
import { nextTick, createAnimation, createSelectorQuery, getEnv, ENV_TYPE } from '@tarojs/taro';
import classNames from 'classnames';
import View from '../view';
import AtIcon from '../icon';

interface INoticebarProps {
  /**
   * css class
   */
  className?: string;
  /**
   * 是否允许换行（当 marquee 值为 true 时，wrap 无效）
   */
  wrap?: boolean;
  /**
   * 通告左侧的图标名称或图片链接
   */
  leftIcon?: string;
  /**
   * 通告右侧的图标名称或图片链接
   */
  rightIcon?: string;
  /**
   * 通知文本颜色，默认取 scss 变量 $ink-noticebar-text-color
   */
  color?: string;
  /**
   * 滚动条背景，默认取 scss 变量 $ink-noticebar-bg-color
   */
  background?: string;
  /**
   * 内容是否滚动（内容只能单行）
   * @default false
   */
  marquee?: boolean;
  /**
   * 内容滚动速度(每秒)
   * @default 75
   */
  speed?: number;
  /**
   * 点击通告栏的回调函数
   */
  onClick?: () => void;
  /**
   * 点击通告右侧图标的回调函数
   */
  onRightIconClick?: () => void;
}

const Noticebar: FC<PropsWithChildren<INoticebarProps>> = ({
  children,
  className = '',
  marquee = false,
  wrap: propsWrap = false,
  speed = 75,
  leftIcon,
  rightIcon,
  color,
  background,
  onClick,
  onRightIconClick,
}) => {
  const [animElemId] = useState(`Ink_${Math.ceil(Math.random() * 10e5).toString(36)}`);
  const [animationData, setAnimationData] = useState({ actions: [{}] });
  const [dura, setDura] = useState(0);
  const [translateX, setTranslateX] = useState(0);

  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const translateTimerRef = useRef<NodeJS.Timeout | null>(null);

  const wrap = marquee ? false : propsWrap;

  const rootClass = classNames(
    'ink-noticebar',
    {
      'ink-noticebar--wrap': wrap,
    },
    className,
  );
  const rootStyle = {
    ...(background ? { background } : undefined),
    ...(color ? { color } : undefined),
  };
  const contentClass = classNames('ink-noticebar__content', {
    'ink-noticebar__content--wrap': wrap,
  });
  const innerClass = classNames('ink-noticebar__content-inner', {
    'ink-noticebar__content-inner--wrap': wrap,
    'ink-noticebar__content-inner--marquee': marquee,
  });

  const cleanScrollTimer = () => {
    if (scrollTimerRef?.current) {
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = null;
    }
  };

  const cleanTranslateTimer = () => {
    if (translateTimerRef?.current) {
      clearTimeout(translateTimerRef.current);
      translateTimerRef.current = null;
    }
  };

  useEffect(() => {
    nextTick(() => {
      if (!marquee) {
        return;
      }
      const query = createSelectorQuery();
      query
        .select(`#${animElemId}`)
        .boundingClientRect()
        .select(`#${animElemId} .ink-noticebar__content-inner`)
        .boundingClientRect()
        .exec((res) => {
          const [contentQueryRes, innerQueryRes] = res || [];
          if (!(contentQueryRes && innerQueryRes)) {
            return;
          }
          const { width: contentQueryWidth } = contentQueryRes;
          const { width: innerQueryWidth } = innerQueryRes;

          // 滚动内容宽度 未超过 content 宽度，则不滚动
          if (contentQueryWidth >= innerQueryWidth) {
            return;
          }
          // 计算滚动完毕耗时
          const duration = innerQueryWidth / speed;

          const resetAnimation = createAnimation({
            duration: 0,
            timingFunction: 'linear',
          });

          const animation = createAnimation({
            duration: duration * 1000,
            timingFunction: 'linear',
          });

          const scroll = () => {
            cleanScrollTimer();

            if (getEnv() === ENV_TYPE.WEB) {
              setDura(0);
              setTranslateX(contentQueryWidth);
              cleanTranslateTimer();
              translateTimerRef.current = setTimeout(() => {
                setDura(duration);
                setTranslateX(-innerQueryWidth);
              }, 100);
            } else {
              setAnimationData(resetAnimation.translate3d(contentQueryWidth, 0, 0).step().export());
              cleanTranslateTimer();
              translateTimerRef.current = setTimeout(() => {
                setAnimationData(animation.translate3d(-innerQueryWidth, 0, 0).step().export());
              }, 100);
            }

            scrollTimerRef.current = setTimeout(scroll, duration * 1000);
          };

          scroll();
        });
    });

    return () => {
      cleanScrollTimer();
      cleanTranslateTimer();
    };
  }, [animElemId, speed, marquee]);

  return (
    <View className={rootClass} style={rootStyle} onClick={onClick}>
      {leftIcon && <AtIcon name={leftIcon} className="ink-noticebar__left-icon" />}
      <View id={animElemId} className={contentClass}>
        <View
          {...(getEnv() === ENV_TYPE.WEB ? { style: { transform: `translate3d(${translateX}px, 0, 0)`, transition: `all ${dura}s linear` } } : { animation: animationData })}
          className={innerClass}
        >
          {children}
        </View>
      </View>
      {rightIcon && (
        <View
          onClick={(e) => {
            e.stopPropagation();
            if (onRightIconClick && typeof onRightIconClick === 'function') {
              onRightIconClick();
            }
          }}
          className="ink-noticebar__right"
        >
          <AtIcon name={rightIcon} className="ink-noticebar__right-icon" />
        </View>
      )}
    </View>
  );
};

export default Noticebar;
